import React from 'react';
import { Glyph } from './glyphs';
import { useCart } from '../../../Contexts/CartContext';

/* THE ACCENT BADGE RULES FOR BOTH TRACKS LIVE IN `patterns/cart/cart.css`, and
   this import is what guarantees they are loaded.
   The desktop nav's cart (`TopNav`) carries the same badge, and until now the
   only thing that pulled `cart.css` in was the cart pattern itself, which is
   imported by `/cart`, the drawer and checkout. A buyer on the marketplace —
   the page where they actually add to the cart — had never loaded it, so the
   badge would have kept the ink colour on the one screen it matters most.
   MarketShell imports this file on every marketplace surface, so the import
   sits here.
   Every rule in the file is scoped to a cart ancestor — `.dsa-cart*`, or the
   `[aria-label^='Cart']` bridge that stands in until `TopNav`'s cart button
   carries `.dsa-cartbtn` — so nothing leaks onto a page with no cart on it. The
   badge override in particular must NOT go back to `.dsk-iconbtn`: that class
   is on every icon button, and `.dsk-cart-badge` is the shared badge class for
   all of them, so the wide selector would paint a notifications bell accent
   against IconButton's own "accent is reserved for state" doctrine. */
import '../../patterns/cart/cart.css';

/**
 * The cart control in the mobile header's right slot.
 *
 * WHY THIS EXISTS, AND WHAT IT REPLACES
 * -------------------------------------
 * Before the conversion the cart lived exactly here — top right of the global
 * header — and it did NOT navigate. It opened the cart drawer, so you could
 * check what was in your cart, adjust quantities and go to checkout without
 * losing the page you were browsing. That was the app's quick-view checkout.
 *
 * The conversion replaced it with a `Cart` tab in the bottom bar pointing at
 * `/checkout`, which was wrong twice over: it cost a fifth of the primary nav,
 * and it SKIPPED the cart entirely, dropping the buyer straight into checkout
 * with no chance to review what they were about to pay for. The drawer itself
 * was converted (`components/Cart/CartOverlay`) but nothing ever mounted it
 * again, because the file that used to — `SellerDashboard/NavigationHeader` —
 * was deleted with the rest of the legacy chrome.
 *
 * So the control is back where it was, and it opens the drawer again. `App.js`
 * mounts the one global `CartOverlay` that this toggles, because the drawer has
 * to outlive any single page's shell.
 *
 * The badge is the real line count from `CartContext`, not a prop, so it cannot
 * drift from the drawer it opens. It is painted in the accent, not in ink, so
 * that adding something to the cart produces a visible change in the header —
 * that is the whole job of this control, and chrome-coloured chrome does not do
 * it. Zero renders NOTHING: a badge reading "0" is noise that trains people to
 * stop looking at the badge.
 */
export function CartButton({ label = 'Cart' }) {
  const cart = useCart();
  const count = cart?.itemCount || 0;

  return (
    <button
      type="button"
      className="ds-iconbtn ds-iconbtn--bare dsa-cartbtn"
      onClick={() => cart?.setShowCartOverlay?.(true)}
      aria-label={count > 0 ? `${label}, ${count} items` : label}
      aria-haspopup="dialog"
    >
      <Glyph name="bag" />
      {count > 0 && (
        <span className="dsa-cartbtn__badge" aria-hidden="true">
          {count}
        </span>
      )}
    </button>
  );
}

export default CartButton;
